import React, { useState } from 'react';
import './App.css';
import LogoUploadPage from './components/LogoUploadPage';
import MockupListPage from './components/MockupListPage';
import aceLogo from './assets/Ace logo.png';

const ZACHS_MOCKUP_IDS = [217430, 217426, 217427,217371, 217370, 217428, 217368, 217355, 217355, 217363];

function App() {
  const [logoFile, setLogoFile] = useState(null);
  const [selectedMockupId, setSelectedMockupId] = useState(null);
  
  // State for single mockup generation
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  // State for Zach's batch mockup generation
  const [zachsMockupResults, setZachsMockupResults] = useState([]);
  const [isGeneratingZachs, setIsGeneratingZachs] = useState(false);
  const [zachsGenerationError, setZachsGenerationError] = useState(null);

  // New state for search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Add state for generated mockups
  const [generatedMockups, setGeneratedMockups] = useState({});
  const [isGeneratingMockups, setIsGeneratingMockups] = useState(false);

  // Add per-mockup loading state
  const [mockupLoading, setMockupLoading] = useState({});

  const handleLogoSelect = (file) => {
    setLogoFile(file);
    setSelectedMockupId(null); 
    setGeneratedImageUrl(null);
    setGenerationError(null);
    setZachsMockupResults([]); // Clear Zach's results on new logo
    setZachsGenerationError(null);
  };

  const handleMockupSelect = (mockupId) => {
    if (!logoFile) return;
    const newSelectedId = selectedMockupId === mockupId ? null : mockupId;
    setSelectedMockupId(newSelectedId);
    setGeneratedImageUrl(null); // Clear single generation if mockup selection changes
    setGenerationError(null);
  };

  const handleGenerateSingleMockup = async () => {
    if (!logoFile || !selectedMockupId) {
      setGenerationError('Please upload a logo and select a mockup first.');
      return;
    }
    setIsGenerating(true);
    setGeneratedImageUrl(null);
    setGenerationError(null);
    const formData = new FormData();
    formData.append('logo', logoFile); 
    formData.append('selectedMockupId', selectedMockupId);
    const API_URL = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(`${API_URL}/api/generate-mockups`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `Server error: ${response.status}`);
      if (data.mockupUrls && data.mockupUrls.length > 0) {
        setGeneratedImageUrl(data.mockupUrls[0]);
      } else {
        throw new Error('Generation successful, but no mockup URL was returned.');
      }
    } catch (err) {
      setGenerationError(err.message || 'An unexpected error occurred during generation.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateZachsMockups = async () => {
    if (!logoFile) {
      setZachsGenerationError('Please upload a logo first to generate Zach\'s mockups.');
      return;
    }
    setIsGeneratingZachs(true);
    setZachsMockupResults([]); // Clear previous results at the start of a new batch
    setZachsGenerationError(null);
    console.log("[App.js] Starting incremental generation for Zach's Mockups with logo:", logoFile.name);

    let errorsEncounteredCount = 0;
    let errorMessages = [];
    const API_URL = process.env.REACT_APP_API_URL || '';

    for (const mockupId of ZACHS_MOCKUP_IDS) {
      console.log(`[App.js] Generating Zach's mockup for ID: ${mockupId}`);
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('selectedMockupId', mockupId.toString());
      try {
        const response = await fetch(`${API_URL}/api/generate-mockups`, {
          method: 'POST',
          body: formData,
        });
        const data = await response.json();
        if (!response.ok) {
          console.error(`[App.js] Error generating Zach's mockup for ID ${mockupId}:`, data.message || response.status);
          errorMessages.push(`ID ${mockupId}: ${data.message || `Server error ${response.status}`}`);
          errorsEncounteredCount++;
          setZachsMockupResults(prevResults => [...prevResults, { id: mockupId, url: null, error: true }]); 
          continue; 
        }
        if (data.mockupUrls && data.mockupUrls.length > 0) {
          setZachsMockupResults(prevResults => [...prevResults, { id: mockupId, url: data.mockupUrls[0], error: false }]);
        } else {
          console.warn(`[App.js] No URL returned for Zach's mockup ID ${mockupId}.`);
          errorMessages.push(`ID ${mockupId}: No URL returned.`);
          errorsEncounteredCount++;
          setZachsMockupResults(prevResults => [...prevResults, { id: mockupId, url: null, error: true }]);
        }
      } catch (err) {
        console.error(`[App.js] Network/fetch error for Zach's mockup ID ${mockupId}:`, err);
        errorMessages.push(`ID ${mockupId}: Network error - ${err.message}`);
        errorsEncounteredCount++;
        setZachsMockupResults(prevResults => [...prevResults, { id: mockupId, url: null, error: true }]);
      }
    }

    if (errorsEncounteredCount > 0) {
      setZachsGenerationError(`Completed Zach's batch. ${errorsEncounteredCount} error(s) occurred. Problematic IDs: ${errorMessages.slice(0,3).join('; ')}${errorMessages.length > 3 ? '...' : ''}`);
    } else {
      setZachsGenerationError(null); // Clear error if all successful
    }
    setIsGeneratingZachs(false);
    console.log("[App.js] Finished incremental generation for Zach's Mockups.");
  };

  // Handlers for search
  const handleSearchInputChange = (event) => setSearchQuery(event.target.value);
  const handleSearchSubmit = () => {
    setActiveSearchTerm(searchQuery);
    setIsSearching(true);
  };
  const handleListAllClick = () => {
    setSearchQuery('');
    setActiveSearchTerm('');
    setIsSearching(false);
  };

  // Handler to generate mockups for all items on the current page, one at a time
  const handleGenerateMockups = async (mockups) => {
    if (!logoFile || !mockups || mockups.length === 0) return;
    setIsGeneratingMockups(true);
    let newGenerated = {};
    let loadingState = {};
    const API_URL = process.env.REACT_APP_API_URL || '';
    // Set all mockups to loading
    mockups.forEach((mockup) => {
      loadingState[mockup.nr] = true;
    });
    setMockupLoading((prev) => ({ ...prev, ...loadingState }));
    for (const mockup of mockups) {
      const formData = new FormData();
      formData.append('logo', logoFile);
      formData.append('selectedMockupId', mockup.nr);
      try {
        const response = await fetch(`${API_URL}/api/generate-mockups`, {
          method: 'POST',
          body: formData,
        });
        const data = await response.json();
        if (data.mockupUrls && data.mockupUrls.length > 0) {
          newGenerated = { ...newGenerated, [mockup.nr]: data.mockupUrls[0] };
          setGeneratedMockups((prev) => ({ ...prev, [mockup.nr]: data.mockupUrls[0] }));
        }
      } catch (err) {
        // Optionally handle error per mockup
      } finally {
        setMockupLoading((prev) => ({ ...prev, [mockup.nr]: false }));
      }
    }
    setIsGeneratingMockups(false);
  };

  // Styles
  const topSectionStyle = { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '20px', marginBottom: '30px', justifyContent: 'center' };
  const sharedBoxStyle = { flex: '0 0 350px', padding: '0px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' };
  const logoUploadBoxStyle = { ...sharedBoxStyle };
  const generationBoxStyle = { ...sharedBoxStyle, padding: '15px', border: '2px solid #28a745', backgroundColor: '#f0fff4', textAlign: 'center' };
  const zachsBatchButtonStyle = { padding: '10px 20px', fontSize: '1em', cursor: 'pointer', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', marginTop: '20px'};
  const resultsGridStyle = { display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', marginTop: '20px' };
  
  // Updated style for Zach's batch result images to match MockupListPage image size
  const zachsResultItemStyle = {
    // Each item will be a flex container for its image and potential text/ID below it
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '10px', // Add some padding around the image + text
    border: '1px solid #eee',
    borderRadius: '4px',
    width: '390px' // To accommodate 360px image + padding (similar to MockupListPage item)
  };
  const zachsResultImageStyle = { 
    width: '360px', 
    height: '360px', 
    border: '1px solid #ccc', 
    objectFit: 'contain', 
    marginBottom: '10px' // Space between image and any text (like ID if it failed)
  };

  // Card container style
  const mainCardStyle = {
    maxWidth: '900px',
    margin: '40px auto 0 auto',
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    padding: '32px 32px 24px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
  };
  const logoStyle = { height: '70px', marginBottom: '8px' };
  const searchRowStyle = { display: 'flex', gap: '10px', width: '100%', justifyContent: 'center', alignItems: 'center', marginBottom: '0' };
  const searchInputStyle = { padding: '10px', border: '1.5px solid #003366', borderRadius: '6px', flex: '1 1 300px', background: '#fff', color: '#003366', fontSize: '1.1em' };
  const buttonStyle = { padding: '10px 18px', cursor: 'pointer', background: '#d7262c', color: '#fff', border: '1.5px solid #003366', borderRadius: '6px', fontWeight: 'bold', fontSize: '1.1em', transition: 'background 0.2s, color 0.2s' };

  return (
    <div className="App">
      <div style={mainCardStyle}>
        <img src={aceLogo} alt="ACE Specialties Logo" style={logoStyle} />
        <div style={{ position: 'relative', width: '100%', marginBottom: '0' }}>
          {/* Overlay the buttons only if logoFile is present */}
          {logoFile && (
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <button
                onClick={() => {}}
                id="generate-mockups-btn"
                style={{
                  ...buttonStyle,
                  minWidth: '220px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  opacity: isGeneratingMockups ? 0.7 : 1,
                  pointerEvents: isGeneratingMockups ? 'none' : 'auto',
                }}
                disabled={isGeneratingMockups}
              >
                {isGeneratingMockups ? 'Generating...' : 'Generate Mockups'}
              </button>
              {/* The blue button will be rendered by LogoUploadPage if logoFile is present */}
            </div>
          )}
          <div style={{ width: '100%', marginTop: '8px' }}>
            <LogoUploadPage logoFile={logoFile} onLogoSelect={handleLogoSelect} showInlineBlueButton={!!logoFile} />
          </div>
        </div>
        <div style={searchRowStyle}>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchInputChange}
            placeholder="Search mockups (e.g., hat, t-shirt)"
            style={searchInputStyle}
          />
          <button onClick={handleSearchSubmit} style={buttonStyle}>Search</button>
          
        </div>
      </div>
      <MockupListPage
        logoFile={logoFile}
        onMockupSelect={handleMockupSelect}
        selectedMockupId={selectedMockupId}
        onGenerateZachsMockups={handleGenerateZachsMockups}
        isGeneratingZachs={isGeneratingZachs}
        zachsBatchButtonStyle={zachsBatchButtonStyle}
        searchQuery={activeSearchTerm}
        isSearching={isSearching}
        generatedMockups={generatedMockups}
        isGeneratingMockups={isGeneratingMockups}
        onGenerateMockups={handleGenerateMockups}
        mockupLoading={mockupLoading}
      />
      <div style={topSectionStyle}>
        {logoFile && selectedMockupId && (
          <div style={generationBoxStyle}>
            <h3>Generate Single Mockup</h3> {/* Changed title for clarity */}
            <p><strong>Logo:</strong> {logoFile.name}</p>
            <p><strong>Selected Mockup ID:</strong> {selectedMockupId}</p>
            <button onClick={handleGenerateSingleMockup} disabled={isGenerating} style={{ padding: '12px 25px', fontSize: '1.1em', cursor: 'pointer', backgroundColor: isGenerating ? '#ccc' : '#007bff', color: 'white', border: 'none', borderRadius: '5px', marginTop: '10px' }}>
              {isGenerating ? 'Generating Your Mockup...' : 'Generate This Mockup'}
            </button>
            {generationError && <p style={{color: 'red', marginTop: '15px'}}><strong>Error:</strong> {generationError}</p>}
            {generatedImageUrl && 
              <div style={{marginTop: '20px'}}>
                <h4>Your Generated Mockup:</h4>
                <img src={generatedImageUrl} alt="Generated Mockup" style={{ maxWidth: '100%', maxHeight: '300px', border: '1px solid #ccc', marginTop: '10px'}}/>
              </div>
            }
          </div>
        )}
      </div>
      
      {/* Section for Zach's Batch Results */}
      {isGeneratingZachs && <p style={{textAlign: 'center', fontSize: '1.2em'}}>Generating Zach's batch, please wait...</p>}
      {zachsGenerationError && <p style={{color: 'red', marginTop: '15px', textAlign: 'center'}}><strong>Zach's Batch Error:</strong> {zachsGenerationError}</p>}
      {zachsMockupResults.length > 0 && (
        <div style={{marginTop: '30px', padding: '20px', borderTop: '2px solid #6c757d'}}>
          <h3 style={{textAlign: 'center'}}>Zach's Batch Mockup Results:</h3>
          <div style={resultsGridStyle}>
            {zachsMockupResults.map((result, index) => (
              // Apply the new item style here
              <div key={`${result.id}-${index}`} style={zachsResultItemStyle}>
                {result.url ? (
                  <img src={result.url} alt={`Zach's Mockup for ID ${result.id}`} style={zachsResultImageStyle} />
                ) : (
                  <div style={{...zachsResultImageStyle, backgroundColor: '#f8d7da', display:'flex', alignItems:'center', justifyContent:'center', color:'#721c24'}}>
                    Failed: ID {result.id}
                  </div>
                )}
                {/* Optionally, display the ID below the image/placeholder */}
                <p style={{fontSize: '0.8em', color: '#555'}}>ID: {result.id}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
